import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Encabezado, Pestanas } from '../navegacion/Marco';
import {
  Aviso,
  Boton,
  Cargando,
  Dato,
  Desplazable,
  ErrorEnPantalla,
  Insignia,
  Panel,
  Vacio,
  clases,
} from '../comun/componentes';
import { useDatos, useRetraso } from '../comun/useDatos';
import { fecha, fechaYHora, gs, iniciales, legible } from '../comun/formato';
import { useSesion } from '../sesion/Sesion';
import { OdontogramaDelPaciente } from '../clinico/OdontogramaDelPaciente';
import { AnamnesisDelPaciente } from '../clinico/AnamnesisDelPaciente';
import { EvolucionesDelPaciente } from '../clinico/EvolucionesDelPaciente';
import type {
  ConsentimientoVista,
  DocumentoClinicoVista,
  FichaPaciente as Ficha,
  PacienteResumen,
  Presupuesto,
} from '../api/tipos';

const SEVERIDAD: Record<string, 'alerta' | 'aviso' | 'marca' | 'neutro'> = {
  Critica: 'alerta',
  Alta: 'alerta',
  Media: 'aviso',
  Baja: 'neutro',
};

/**
 * Ficha 360°. Arriba, lo que hay que ver sin pensar: quién es y qué alerta clínica tiene.
 * Debajo, la profundidad por pestañas, que es el progressive disclosure del encargo.
 */
export function FichaPaciente() {
  const { id = '' } = useParams();
  const { puede } = useSesion();
  const [pestana, setPestana] = useState('resumen');

  const { datos: ficha, cargando, error, recargar } = useDatos<Ficha>(`/api/v1/pacientes/${id}`);

  if (cargando) {
    return <Cargando que="la ficha" />;
  }

  if (error || !ficha) {
    return <ErrorEnPantalla mensaje={error ?? 'No se encontró la ficha.'} reintentar={recargar} />;
  }

  const criticas = ficha.alertas.filter((a) => a.severidad === 'Critica' || a.severidad === 'Alta');

  return (
    <>
      <Encabezado
        titulo={`${ficha.apellidos}, ${ficha.nombres}`}
        detalle={[
          ficha.documentos[0],
          ficha.edad != null ? `${ficha.edad} años` : null,
          ficha.telefonoPrincipal,
        ]
          .filter(Boolean)
          .join(' · ')}
        accion={
          <div className="flex flex-wrap gap-2">
            {puede('agenda.agendar') && (
              <Link to={`/agenda?paciente=${ficha.id}`}>
                <Boton>Agendar</Boton>
              </Link>
            )}
            {puede('paciente.exportar') && (
              <a
                href={`/api/v1/pacientes/${ficha.id}/historia/imprimible`}
                target="_blank"
                rel="noreferrer"
              >
                <Boton variante="secundario">Historia imprimible</Boton>
              </a>
            )}
          </div>
        }
      />

      {/* Las alertas clínicas van antes que todo: es lo que evita el accidente. */}
      {criticas.length > 0 && (
        <div className="mb-4 space-y-2">
          {criticas.map((alerta) => (
            <Aviso key={alerta.codigo} tono="alerta">
              <strong className="font-semibold">{alerta.nombre}</strong>
              {alerta.detalle ? ` — ${alerta.detalle}` : ''}
            </Aviso>
          ))}
        </div>
      )}

      <Pestanas
        actual={pestana}
        alCambiar={setPestana}
        opciones={[
          { valor: 'resumen', rotulo: 'Resumen' },
          { valor: 'odontograma', rotulo: 'Odontograma' },
          { valor: 'anamnesis', rotulo: 'Anamnesis', contador: ficha.alertas.length },
          { valor: 'evoluciones', rotulo: 'Evoluciones' },
          { valor: 'presupuestos', rotulo: 'Presupuestos' },
          { valor: 'documentos', rotulo: 'Documentos' },
        ]}
      />

      {pestana === 'resumen' && <Resumen ficha={ficha} />}
      {pestana === 'odontograma' && <OdontogramaDelPaciente pacienteId={ficha.id} />}
      {pestana === 'anamnesis' && (
        <AnamnesisDelPaciente pacienteId={ficha.id} alGuardar={recargar} />
      )}
      {pestana === 'evoluciones' && <EvolucionesDelPaciente pacienteId={ficha.id} />}
      {pestana === 'presupuestos' && <PresupuestosDelPaciente pacienteId={ficha.id} />}
      {pestana === 'documentos' && <DocumentosDelPaciente pacienteId={ficha.id} />}
    </>
  );
}

function Resumen({ ficha }: { ficha: Ficha }) {
  return (
    <div className="grid gap-4 portatil:grid-cols-3">
      <Panel className="portatil:col-span-2" titulo="Datos de la ficha">
        <dl className="grid gap-4 tablet:grid-cols-3">
          <Dato rotulo="Documento">
            {ficha.documentos.length ? ficha.documentos.join(' · ') : 'Sin documento cargado'}
          </Dato>
          <Dato rotulo="Nacimiento">{fecha(ficha.fechaNacimiento)}</Dato>
          <Dato rotulo="Sexo">{legible(ficha.sexo)}</Dato>
          <Dato rotulo="Teléfono">{ficha.telefonoPrincipal ?? '—'}</Dato>
          <Dato rotulo="Correo">{ficha.email ?? '—'}</Dato>
          <Dato rotulo="Ciudad">{ficha.ciudad ?? '—'}</Dato>
          <Dato rotulo="Dirección" className="tablet:col-span-2">
            {ficha.direccion ?? '—'}
          </Dato>
          <Dato rotulo="Última visita">{fecha(ficha.ultimaVisita)}</Dato>
        </dl>

        {ficha.esMenor && !ficha.tieneRepresentanteLegal && (
          <div className="mt-4">
            <Aviso tono="aviso">
              Es paciente menor y todavía no tiene representante legal vinculado. Sin él, el
              consentimiento no se puede firmar.
            </Aviso>
          </div>
        )}
      </Panel>

      <div className="space-y-4">
        <Panel titulo="Alertas clínicas">
          {ficha.alertas.length === 0 ? (
            <Vacio
              titulo="Sin alertas"
              detalle="Cargá la anamnesis para que las alergias y los antecedentes aparezcan acá."
            />
          ) : (
            <ul className="space-y-2">
              {ficha.alertas.map((alerta) => (
                <li key={alerta.codigo} className="flex items-start justify-between gap-2">
                  <span className="min-w-0">
                    <span className="block text-sm text-tinta-800">{alerta.nombre}</span>
                    {alerta.detalle && (
                      <span className="block text-dato text-tinta-500">{alerta.detalle}</span>
                    )}
                  </span>
                  <Insignia tono={SEVERIDAD[alerta.severidad] ?? 'neutro'}>
                    {alerta.severidad}
                  </Insignia>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel titulo="Historia">
          <dl className="space-y-3">
            <Dato rotulo="Anamnesis">
              {ficha.anamnesisVersion ? `versión ${ficha.anamnesisVersion}` : 'sin cargar'}
            </Dato>
            <Dato rotulo="Odontograma">
              {ficha.odontogramaVersion ? `versión ${ficha.odontogramaVersion}` : 'sin cargar'}
            </Dato>
          </dl>
        </Panel>
      </div>
    </div>
  );
}

function PresupuestosDelPaciente({ pacienteId }: { pacienteId: string }) {
  const { datos, cargando, error, recargar } = useDatos<Presupuesto[]>(
    `/api/v1/pacientes/${pacienteId}/presupuestos`,
  );

  if (cargando) return <Cargando que="los presupuestos" />;
  if (error) return <ErrorEnPantalla mensaje={error} reintentar={recargar} />;

  if (!datos || datos.length === 0) {
    return (
      <Panel>
        <Vacio
          titulo="Sin presupuestos"
          detalle="Los presupuestos se arman desde un plan de tratamiento, en el módulo Tratamientos."
        />
      </Panel>
    );
  }

  return (
    <Panel titulo="Presupuestos del paciente">
      <Desplazable>
        <table className="tabla">
          <thead>
            <tr>
              <th scope="col">N.º</th>
              <th scope="col">Estado</th>
              <th scope="col">Vigencia</th>
              <th scope="col" className="text-right">Total</th>
              <th scope="col" className="text-right">Convenio</th>
              <th scope="col" className="text-right">Paciente</th>
            </tr>
          </thead>
          <tbody>
            {datos.map((presupuesto) => (
              <tr key={presupuesto.id}>
                <td className="cifra whitespace-nowrap font-medium">
                  {presupuesto.numero}/{presupuesto.version}
                </td>
                <td>
                  <Insignia tono={presupuesto.estado === 'Aceptado' ? 'exito' : 'neutro'}>
                    {legible(presupuesto.estado)}
                  </Insignia>
                </td>
                <td className="cifra whitespace-nowrap text-dato">
                  {fecha(presupuesto.vigenciaDesde)} – {fecha(presupuesto.vigenciaHasta)}
                </td>
                <td className="cifra text-right">{gs(presupuesto.total)}</td>
                <td className="cifra text-right">{gs(presupuesto.totalConvenio)}</td>
                <td className="cifra text-right font-medium">{gs(presupuesto.totalPaciente)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Desplazable>
    </Panel>
  );
}

function DocumentosDelPaciente({ pacienteId }: { pacienteId: string }) {
  const documentos = useDatos<DocumentoClinicoVista[]>(
    `/api/v1/pacientes/${pacienteId}/documentos`,
  );
  const consentimientos = useDatos<ConsentimientoVista[]>(
    `/api/v1/pacientes/${pacienteId}/consentimientos`,
  );

  return (
    <div className="grid gap-4 portatil:grid-cols-2">
      <Panel titulo="Recetas y órdenes">
        {documentos.cargando && <Cargando que="los documentos" />}
        {documentos.error && <ErrorEnPantalla mensaje={documentos.error} />}
        {documentos.datos?.length === 0 && (
          <Vacio titulo="Sin documentos emitidos" detalle="Las recetas se emiten al cerrar la sesión clínica." />
        )}
        {documentos.datos && documentos.datos.length > 0 && (
          <ul className="space-y-3">
            {documentos.datos.map((documento) => (
              <li
                key={documento.id}
                className={clases(
                  'rounded-lg border border-papel-200 p-3',
                  documento.estado === 'Anulada' && 'opacity-60',
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="text-sm font-medium text-tinta-800">{legible(documento.tipo)}</span>
                  <Insignia tono={documento.estado === 'Anulada' ? 'alerta' : 'exito'}>
                    {legible(documento.estado)}
                  </Insignia>
                </div>
                <p className="mt-1 whitespace-pre-line text-dato text-tinta-600">
                  {documento.contenido}
                </p>
                <p className="mt-1 text-dato text-tinta-400">{fechaYHora(documento.emitidoEn)}</p>
                {documento.anuladoMotivo && (
                  <p className="mt-1 text-dato text-alerta-700">Anulada: {documento.anuladoMotivo}</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel titulo="Consentimientos" descripcion="Ley N° 7593/2025 de protección de datos">
        {consentimientos.cargando && <Cargando que="los consentimientos" />}
        {consentimientos.datos?.length === 0 && (
          <Vacio
            titulo="Sin consentimientos"
            detalle="El paciente todavía no aceptó el tratamiento de sus datos."
          />
        )}
        {consentimientos.datos && consentimientos.datos.length > 0 && (
          <ul className="space-y-3">
            {consentimientos.datos.map((consentimiento) => (
              <li key={consentimiento.id} className="rounded-lg border border-papel-200 p-3">
                <div className="flex items-start justify-between gap-2">
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-tinta-800">
                      {consentimiento.textoTitulo}
                    </span>
                    <span className="block text-dato text-tinta-500">
                      versión {consentimiento.textoVersion} · {legible(consentimiento.formaObtencion)}
                    </span>
                  </span>
                  <Insignia tono={consentimiento.estaVigente ? 'exito' : 'alerta'}>
                    {consentimiento.estaVigente ? 'Vigente' : 'Revocado'}
                  </Insignia>
                </div>
                {consentimiento.finalidades.length > 0 && (
                  <p className="mt-1 text-dato text-tinta-500">
                    Finalidades: {consentimiento.finalidades.map(legible).join(', ')}
                  </p>
                )}
                <p className="mt-1 text-dato text-tinta-400">
                  {fechaYHora(consentimiento.otorgadoEn)}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}

/** Avatar reutilizable: la ficha y la agenda muestran al paciente igual. */
export function Avatar({ nombre }: { nombre: string }) {
  return (
    <span
      aria-hidden="true"
      className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-marca-50 text-dato font-semibold text-marca-700"
    >
      {iniciales(nombre)}
    </span>
  );
}

/**
 * Buscador de paciente por nombre o documento, reutilizado por agenda, planes y cobro: los
 * tres necesitan lo mismo —tipear, ver el documento para desambiguar homónimos y confirmar
 * a quién se eligió—, así que viven de un solo lugar en vez de tres copias que divergen.
 */
export function BuscadorDePaciente({
  id,
  etiqueta = 'Paciente',
  elegidoId,
  alElegir,
  ocultarListaTrasElegir = false,
}: {
  id: string;
  etiqueta?: string;
  elegidoId?: string | null;
  alElegir: (paciente: PacienteResumen) => void;
  ocultarListaTrasElegir?: boolean;
}) {
  const [busqueda, setBusqueda] = useState('');
  const buscado = useRetraso(busqueda);

  const { datos: pacientes } = useDatos<PacienteResumen[]>(
    buscado.trim() ? `/api/v1/pacientes?q=${encodeURIComponent(buscado.trim())}` : null,
    [buscado],
  );

  const mostrarLista = pacientes && pacientes.length > 0 && !(ocultarListaTrasElegir && elegidoId);

  return (
    <div>
      <label className="etiqueta-campo" htmlFor={id}>
        {etiqueta}
      </label>
      <input
        id={id}
        type="search"
        value={busqueda}
        onChange={(e) => setBusqueda(e.target.value)}
        placeholder="Buscá por nombre o documento…"
        className="campo mt-1"
      />

      {mostrarLista && (
        <ul className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-papel-300">
          {pacientes.slice(0, 12).map((candidato) => (
            <li key={candidato.id}>
              <button
                type="button"
                onClick={() => {
                  alElegir(candidato);
                  setBusqueda(candidato.nombreCompleto);
                }}
                className={clases(
                  'flex min-h-tactil w-full items-center justify-between gap-2 px-3 text-left text-sm',
                  elegidoId === candidato.id ? 'bg-marca-50 text-marca-700' : 'hover:bg-papel-50',
                )}
              >
                <span className="truncate">{candidato.nombreCompleto}</span>
                <span className="cifra shrink-0 text-dato text-tinta-400">{candidato.documento ?? ''}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {elegidoId && (
        <Insignia tono="exito" className="mt-2">
          Paciente elegido
        </Insignia>
      )}
    </div>
  );
}
